import React, { useRef, useState, useEffect, useCallback } from 'react'

import { bool, string, number, node } from 'prop-types'

import Tooltip from './Tooltip'

function SmartTooltip({
  autoPosition = false,
  containerSelector = null,
  autoPositionThreshold = 100,
  place = null,
  wrapperTag: WrapperTag = 'div',
  children,
  ...tooltipProps
}) {
  const wrapperRef = useRef(null)
  const [isClient, setIsClient] = useState(false)
  const [calculatedPlace, setCalculatedPlace] = useState('top')

  useEffect(() => {
    setIsClient(true)
  }, [])

  const calculatePosition = useCallback(() => {
    const element = wrapperRef.current
    if (!element) return

    const trigger = element.querySelector('[data-tip]')
    if (!trigger) return

    const elementRect = trigger.getBoundingClientRect()
    const parent = containerSelector ? trigger.closest(containerSelector) : null
    const leftPosition = parent
      ? elementRect.left - parent.getBoundingClientRect().left
      : elementRect.left

    setCalculatedPlace(leftPosition < autoPositionThreshold ? 'right' : 'top')
  }, [containerSelector, autoPositionThreshold])

  useEffect(() => {
    if (!autoPosition || !isClient) return

    // Measure at idle: getBoundingClientRect forces a sync reflow when it runs
    // interleaved with other components' mount-time DOM writes. By idle time
    // layout is clean, so the read is cheap. Until it runs the tooltip keeps
    // the 'top' default, same as today before the measurement.
    let idleId = null
    let timer = null

    if ('requestIdleCallback' in window) {
      idleId = requestIdleCallback(calculatePosition, { timeout: 2000 })
    } else {
      // Fallback for browsers without requestIdleCallback (e.g., Safari):
      // same timing as the previous implementation.
      timer = setTimeout(calculatePosition, 0)
    }

    let resizeTimeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(calculatePosition, 100)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (idleId !== null) cancelIdleCallback(idleId)
      if (timer !== null) clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [autoPosition, isClient, calculatePosition])

  const finalPlace = place || (autoPosition ? calculatedPlace : 'top')

  // wrapperTag is forwarded to Tooltip so both the outer container and the inner
  // trigger use the same element. Both must match: if children are block elements
  // (e.g. TagComponent's <div>), a <span> trigger still causes browser HTML
  // auto-correction and a React hydration mismatch.
  return (
    <WrapperTag ref={wrapperRef}>
      <Tooltip {...tooltipProps} place={finalPlace} wrapperTag={WrapperTag}>
        {children}
      </Tooltip>
    </WrapperTag>
  )
}

SmartTooltip.propTypes = {
  autoPosition: bool,
  containerSelector: string,
  autoPositionThreshold: number,
  place: string,
  wrapperTag: string,
  children: node.isRequired,
}

export default SmartTooltip
